import { addDays, isBefore } from 'date-fns'
import { action, computed, makeObservable, observable } from 'mobx'

import stringUtils from '@/utils/String/stringUtils'

import { DTO } from '../DTO'

export class Academy extends DTO {
  academyId?: res.Academy['academyId']
  status: res.Academy['status'] = 'FREE_INPROGRESS'
  name: res.Academy['name'] = ''
  email: res.Academy['email'] = ''
  logoUrl: res.Academy['logoUrl'] = ''
  initialized: res.Academy['initialized'] = false
  initializedDate: Date | null = null
  cellPhone: res.Academy['cellPhone'] = ''
  sellerCode: res.Academy['sellerCode'] = ''
  freeStartDate: res.Academy['freeStartDate'] = ''
  freeEndDate: res.Academy['freeEndDate'] = ''
  payDate?: res.Academy['payDate']
  payStartDate: res.Academy['payStartDate'] = ''
  payEndDate: res.Academy['payEndDate'] = ''
  freePremiumStartDate: res.Academy['freePremiumStartDate'] = ''
  freePremiumEndDate: res.Academy['freePremiumEndDate'] = ''
  logoUrlDateNow? = new Date().getTime()
  worksheetShareFlag: res.Academy['worksheetShareFlag'] = false
  studentCount: res.Academy['studentCount'] = 0
  teacherCount: res.Academy['teacherCount'] = 0
  educationCourses: res.Academy['educationCourses'] = []
  subjects: res.Academy['subjects'] = []
  lessonClassCount = 0

  monthlyBaseFee: res.Academy['monthlyBaseFee'] = 0
  customerType: res.Academy['customerType'] = null

  address: res.Academy['address'] = ''
  addressDetail: res.Academy['addressDetail'] = ''
  zipCode: res.Academy['zipCode'] = ''

  가상학생등록여부 = false
  subscribePlan: res.AcademyPayment['subscribePlan'] = null
  additionalPricingPlan?: res.Academy['additionalPricingPlan']
  authority: res.Academy['authority'] = 'BASIC'

  subscribeItems: res.Academy['subscribeItems'] = []
  studentLimitCount: res.Academy['studentLimitCount'] = 0
  readonly specialContractPlans: res.Academy['specialContractPlans'] = []

  constructor(data?: res.Academy) {
    super()

    makeObservable<this>(this, {
      status: observable,
      name: observable,
      email: observable,
      logoUrl: observable,
      initialized: observable,
      initializedDate: observable,
      cellPhone: observable,
      sellerCode: observable,
      freeStartDate: observable,
      freeEndDate: observable,
      payDate: observable,
      payStartDate: observable,
      payEndDate: observable,
      monthlyBaseFee: observable,
      customerType: observable,
      logoUrlDateNow: observable,
      worksheetShareFlag: observable,
      studentCount: observable,
      lessonClassCount: observable,
      가상학생등록여부: observable,
      academyId: observable,
      logoUrlAdjusted: computed,
      payStatus: computed,
      isNoneLogoUrl: computed,
      isSales: computed,
      isFreeInProgressSales: computed,
      isFreeInProgress: computed,
      needCustomerTypeChange: computed,
      setCustomerType: action,
      subscribePlan: observable,
      additionalPricingPlan: observable,
      authority: observable,
      subscribeItems: observable,
      studentLimitCount: observable,
      specialContractPlans: observable,
    })

    if (data) {
      this.update<Academy>({
        ...data,
        initializedDate: data.initializedDate ? new Date(data.initializedDate) : null,
      })
    }
  }

  get payStatus() {
    switch (this.status) {
      case 'PAY_INPROGRESS':
        return '정식가입'
      case 'PAY_SCHEDULED':
        return '정식가입 예정'
      case 'FREE_INPROGRESS':
      default:
        return '무료체험'
    }
  }

  get dashedPhone() {
    try {
      if (this.cellPhone) {
        const number = this.cellPhone.match(/\d+/g)?.join('') || ''
        return stringUtils.toDashedPhone(number) ?? this.cellPhone
      }
      return '-'
    } catch (error) {
      return this.cellPhone
    }
  }

  get logoUrlAdjusted() {
    return `${this.logoUrl}${this.logoUrlDateNow ? `?${this.logoUrlDateNow}` : ''}`
  }

  get isNoneLogoUrl() {
    return this.logoUrl.includes('None.png')
  }

  get isSales() {
    return this.sellerCode && this.sellerCode.startsWith('SYS')
  }

  get isFreeInProgressSales() {
    return this.isFreeInProgress && this.isSales
  }

  get isFranchise() {
    return this.customerType === 'B2B-프랜차이즈'
  }

  get isFreeInProgress() {
    return this.status === 'FREE_INPROGRESS'
  }

  get needCustomerTypeChange() {
    const targetTypes = ['전화 분류 필요', '분류 필요']

    return (
      this.status === 'PAY_INPROGRESS' &&
      (!this.customerType || targetTypes.includes(this.customerType))
    )
  }

  get subscriptionPlanType() {
    switch (this.subscribePlan) {
      case 'PREMIUM':
      case 'PREMIUM_PLUS':
      case 'PREMIUM_CUSTOM':
      case 'PREMIUM_UNLIMITED':
      case 'PREMIUM_STUDENT_CONTRACT':
        return '프리미엄요금제'
      default:
        return '베이직요금제'
    }
  }

  get isPremiumPlan() {
    return this.subscriptionPlanType === '프리미엄요금제'
  }

  get isStudentContractPlan() {
    return this.subscribePlan === 'PREMIUM_STUDENT_CONTRACT'
  }

  get isPayStartDateIn90Days() {
    if (!this.payStartDate) return false

    return isBefore(new Date(), addDays(new Date(this.payStartDate), 90))
  }

  setCustomerType(type: res.Academy['customerType']) {
    this.customerType = type
  }
}
